<template>
  <div class="photo-upload-content">
    <exit-survey v-if="!isMobile"/>
    <div class="mb-3" style="margin-top: 20px">
      <b-overlay
        id="img-upload-process-modal"
        :show="showProcess"
        z-index="9999999"
        @shown="$refs.imgUploadProcess.focus()"
      >
        <template #overlay>
          <div
            ref="imgUploadProcess"
            class="text-center p-4 bg-success text-light rounded"
          >
            <b-icon icon="cloud-upload" font-scale="4"></b-icon>
            <div class="mb-3">{{ imgProcessText }}</div>
            <b-progress
              min="0"
              :max="processMaxWaitTime"
              :value="processAt"
              variant="primary"
              height="3px"
              class="mx-n4 rounded-0"
            ></b-progress>
          </div>
        </template>
        <div class="photo-upload rounded-2 mb-4">
          <div class="px-2 pb-5 pt-2 photo-upload-wrapper">
            <div class="upload-section">
              <h1
                class="font-weight-bold text-left fs-20 content-title"
                :class="{
                  'guided-upload-no-err': !(showImgSizeErr || showImgTypeErr || showMaxLengthErr),
                }"
                :style="!isMobile && 'padding-left: 1.8rem'"
              >
                ADD CAR PHOTOS
              </h1>
              <!-- Mobile alert banner -->
              <Banner iconName="alert_triangle_red" dismissible @dismissed="showImgSizeErr=false" bannerType="alert" v-if="showImgSizeErr && isMobile" class="photo-description font-weight-bold">
                One or more photos were NOT added.<br>Please upload images that are less than 20MB.
              </Banner>
              <Banner iconName="alert_triangle_red" dismissible @dismissed="showImgTypeErr=false" bannerType="alert" v-if="showImgTypeErr && isMobile" class="photo-description font-weight-bold">
                One or more photos were NOT added.<br>Please upload images in the following file types: jpeg, png, tiff.
              </Banner>
              <Banner iconName="alert_triangle_red" dismissible @dismissed="showMaxLengthErr=false" bannerType="alert" v-if="showMaxLengthErr && isMobile" class="photo-description font-weight-bold" :style="!showPhotoUploadBox ? 'margin-bottom: unset' : ''">
                One or more photos were NOT added.<br>Please upload up to {{ maxLength }} images.
              </Banner>
              <PhotoUploadBox
                v-if="showPhotoUploadBox"
                @uploadPhoto="uploadPhoto"
                :isMobile="isMobile"
                :handleDrop="drop"
                :imageDeleting="imageDeleting"
                :maxLength="maxLength"
                :errImgType="errImgType"
                :initImgUploadCamera="initImgUploadCamera"
                :isSpecialDeviceBrowser="isSpecialDeviceBrowser"
              />
              <div class="upload-tips">
                <!-- Photo guide banner -->
                <Banner iconName="digital_camera_blue" v-if="!isUploadedImages" class="photo-description font-weight-bold">
                  Add at least 10 photos and increase your chance of selling by 55%! <a :href="autoflipURL+'/photo-guide'" target="_blank">Learn more</a>
                </Banner>
                <!-- Dynamic stats banner -->
                <Banner iconName="energy" v-else-if="uploadImage.length < 10" class="photo-description font-weight-bold">
                  Psst! Add at least {{ photosNeeded }} more photos to increase your chance of selling by {{ photosUploadPercent }}.
                </Banner>
                <!-- Reminder banner -->
                <Banner iconClass="reminder-icon" v-if="isUploadedImages" class="photo-description font-weight-bold">
                  Don't forget to add photos of your car's odometer, tyres, and all pages of your service history.
                </Banner>
                <!-- Alert banner -->
                <Banner iconName="alert_triangle_red" dismissible @dismissed="showImgSizeErr=false" bannerType="alert" v-if="showImgSizeErr && !isMobile" class="photo-description font-weight-bold">
                  One or more photos were NOT added.<br>Please upload images that are less than 20MB.
                </Banner>
                <Banner iconName="alert_triangle_red" dismissible @dismissed="showImgTypeErr=false" bannerType="alert" v-if="showImgTypeErr && !isMobile" class="photo-description font-weight-bold">
                  One or more photos were NOT added.<br>Please upload images in the following file types: jpeg, png, tiff.
                </Banner>
                <Banner iconName="alert_triangle_red" dismissible @dismissed="showMaxLengthErr=false" bannerType="alert" v-if="showMaxLengthErr && !isMobile" class="photo-description font-weight-bold">
                  One or more photos were NOT added.<br>Please upload up to {{ maxLength }} images.
                </Banner>
              </div>
            </div>
            <div class="position-relative d-flex flex-column align-items-center">
              <div class="photo-div">
                <draggable
                  v-model="uploadImage"
                  class="photo-list wide-list p-0"
                  tag="ol"
                  :ghost-class="'dragging'"
                  :animation="200"
                  :delay="150"
                  :delay-on-touch-only="true"
                  :touch-start-threshold="5"
                  :disabled="!dragEnabled"
                  @end="onReorderEnd"
                >
                  <li
                    v-for="(i, index) in uploadImage"
                    class="m-1 item"
                    :class="[index === 0 ? 'li-cover-photo' : '', i.err ? 'uploaded-fail' : '']"
                    :key="i.id"
                    style="position: relative"
                    :style="dragEnabled && 'cursor: move'"
                  >
                    <div class="image-container">
                      <b-overlay
                        :show="i.onUpload"
                        no-wrap
                        spinner-variant="success"
                        :opacity="0.5"
                      ></b-overlay>
                      <img :class="i.err ? 'img-upload-fail' : ''" :data-img-id="i.id" width="100%" :src="i.url" @contextmenu="(e) => doNothing(e)" />
                      <div v-if="i.err" class="upload-fail-text d-flex align-items-center">
                        <img src="@/assets/images/icons/ic_upload_fail.svg" class="upload-fail-icon" alt="">
                        <p class="font-weight-thin">Image upload failed</p>
                      </div>
                      <b-progress
                        v-if="i.progress && i.progress !== 100"
                        class="img-upload-progress"
                        :value="i.progress"
                        variant="success"
                        striped
                      ></b-progress>
                      <div class="edit-options-list" v-show="!i.onUpload" v-if="!i.err">
                        <v-touch v-on:tap="deleteImg(i.filename, i.key, i.id)">
                          <custom-icon
                            name="delete"
                            class="edit-photo right"
                            width="100%"
                            :m0="true"
                            @click="deleteImg(i.filename, i.key, i.id)"
                          />
                        </v-touch>
                        <v-touch>
                          <custom-icon
                            :data-img-id="i"
                            name="roll"
                            class="edit-photo left bottom-left"
                            width="100%"
                            @click="rotate(i)"
                          />
                        </v-touch>
                      </div>
                    </div>
                  </li>
                </draggable>
              </div>
            </div>
            <div
              style="width: 100%; display: flex; justify-content: flex-end"
              class="px-3 pb-2"
            >
            </div>
            <div style="height: 30px;"></div>
            <btn-group
              class=""
              @onOk="next"
              @onCancel="goBack"
              @onSkip="goNext"
              :okBtnDisable="uploadImage.length >= minLength && !isHavingUploadIssue"
              :disableNext="uploadImage.length < minLength || onUpload || isHavingUploadIssue"
              :disableBack="onUpload"
              :disableSkip="uploadImage.length"
              :query=this.$route.query
              :isMobile=isMobile
              :customClasses="`${btnGroupPadding} skip-able`"
              :isHavingUploadIssue="isHavingUploadIssue"
            />
            <div v-if="isMobile" class="bottom-btns"></div>
          </div>
        </div>
      </b-overlay>
    </div>
    <!-- <b-modal id="init-upload-modal" hide-footer modal-class="modal-init-upload">
      <template #modal-header>
        <b-button
          type="button"
          aria-label="Close"
          variant="outline-light"
          pill
          style="border: 0; font-size: 25px; position: absolute; right: 0"
          @click="!isAccessingCamera && $bvModal.hide('init-upload-modal')"
        >
          <b-icon icon="x-circle"></b-icon>
        </b-button>
      </template>
      <div class="d-block text-center">
        <div class="upload-option-wrapper">
          <b-overlay
            :show="isAccessingCamera"
            rounded
            opacity="0.6"
            spinner-small
            class="d-inline-block"
          >
            <div
              class="upload-option upload-camera"
              @click="initImgUploadCamera"
            >
              <div>
                <b-icon class="option-icon" icon="camera-fill"></b-icon>
              </div>
              <div><b>Guided camera mode</b></div>
            </div>
          </b-overlay>

          <div
            class="upload-option upload-file"
            @click="!isAccessingCamera && initImgUploadFile()"
          >
            <div><b-icon class="option-icon" icon="images"></b-icon></div>
            <div><b>Folder</b></div>
          </div>
        </div>
      </div>
    </b-modal> -->
    <b-modal
      no-close-on-backdrop
      hide-header
      hide-footer
      id="camera-modal"
      modal-class="camera-modal"
      ref="cameraModal"
    >
      <div class="camera-screen">
        <video
          ref="cameraView"
          class="camera-view"
          autoplay
          playsinline
          muted
        ></video>
        <div v-if="!showCameraOption" class="confirm-option-wrapper d-flex justify-content-center text-center">
          <b-overlay
            rounded
            opacity="0.6"
            spinner-small
            class="d-inline-block overlay"
          >
            <div
              class="cursor-pointer confirm-option use-this-photo"
              style="height: 210px;"
              @click="!isSavingCameraPhoto && !showCameraOption ? exitCamera() : enableExitConfirmDialog()"
            >
              <b class="confirm-text save-exit-text">Save & Exit Camera</b>
            </div>
          </b-overlay>
        </div>
        <div v-if="showCameraOption" class="confirm-option-wrapper d-flex justify-content-center text-center">
          <div
            class="cursor-pointer confirm-option take-again"
            @click="takePhotoAgain"
          >
            <b class="confirm-text">Try Again</b>
            <b-icon icon="arrow-repeat" class="confirm-icon"></b-icon>
          </div>
          <b-overlay
            :show="isSavingCameraPhoto"
            rounded
            opacity="0.6"
            spinner-small
            class="d-inline-block overlay"
          >
            <div
              class="cursor-pointer confirm-option use-this-photo"
              @click="useThisPhoto"
            >
              <b class="confirm-text">Use This Photo</b>
              <b-icon icon="check" class="confirm-icon"></b-icon>
            </div>
          </b-overlay>
        </div>
        <div v-if="showCameraOption && isClosing" class="confirm-option-wrapper d-flex justify-content-center text-center">
          <div
            class="cursor-pointer confirm-option take-again"
            @click="discardAndExit"
          >
            <b class="confirm-text">Discard Photos</b>
            <!-- <b-icon icon="arrow-repeat" class="confirm-icon"></b-icon> -->
          </div>
          <b-overlay
            :show="isSavingCameraPhoto"
            rounded
            opacity="0.6"
            spinner-small
            class="d-inline-block overlay"
          >
            <div
              class="cursor-pointer confirm-option use-this-photo"
              @click="saveAndExit"
            >
              <b class="confirm-text">Save</b>
              <!-- <b-icon icon="check" class="confirm-icon"></b-icon> -->
            </div>
          </b-overlay>
        </div>

        <Tooltips v-if="showHelpGuide"/>
        
        <div v-if="currentCapturePhoto.frame" id="camera-outlay" :class="{ 'camera-outlay-buildplate': currentCapturePhoto.key === 'interiorBuildPlate' }">
          <img
            :src="require(`@/assets/car-frames/${currentCapturePhoto.frame}`)"
            alt=""
          />
        </div>
        <canvas ref="cameraOutput" id="camera-output" @click="closeDropdown"></canvas>
        <div class="function-section">
          <custom-icon name="capture" class="shoot-btn" width="100%" :m0="true" @click="!isSavingCameraPhoto && takePhoto()"/>
          <div class="button-wrapper">
            <div class="under-btn">
              <b-button
                class="body-type-btn"
                @click="!isSavingCameraPhoto && !showCameraOption && toggleBodyTypePanel()"
              >
                <custom-icon name="body_type" width="100%" :m0="true"/>
              </b-button>
              <b-button
                class="help-btn"
                :class="{ active: showHelpGuide }"
                @click="!isSavingCameraPhoto && !showCameraOption && openHelpGuide()"
              >
                <b-icon class="shoot-btn-icon" icon="question"></b-icon
              ></b-button>
            </div>
            <div class="upper-btn">
              <b-button
                class="close-btn"
                @click="!isSavingCameraPhoto && !showCameraOption ? exitCamera() : saveAndExitWithPhoto()"
                :style="this.uploadImage.length >= 1 ? 'background: #1f8252' : ''"
              >
                <b-icon v-if="this.uploadImage.length >= 1" icon="check2" scale="2" ></b-icon>
                <custom-icon v-else name="close" width="100%" :m0="true"/>
              </b-button>
            </div>
          </div>
        </div>
        <div class="body-type-dropdown" v-if="showBodyTypePanel">
          <ul>
            <li v-for="(carBodyType, index) in bodyTypes" :key="index">
              <div class="option" @click="updateBodyType(carBodyType)">
                <div class="body-type-name">
                  {{ carBodyType }}
                </div>
                <div class="body-type-status">
                  <b-icon v-if="carBodyType.toLowerCase() === curBodyType.toLowerCase()" icon="check" scale="2"></b-icon>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div class="photo-frame-wrapper">
          <div class="photo-frame-dropdown">
            <div class="photo-image-list-wrapper" v-if="showFrameSelection">
              <ul>
                <li v-for="(photo, index) in photoOrdersFiltered" :key="index"  @click="setCurrentPhoto(index)">
                  <div class="option">
                    <div class="frame-type">
                      {{ photo.type }}
                    </div>
                    <div class="frame-name">
                      {{ photo.title }}
                    </div>
                    <div class="frame-status">
                      <b-icon v-if="photo.haveImg" icon="check" scale="2"></b-icon>
                      <b-icon v-else icon="dot" scale="3"></b-icon>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div class="photo-frame-header">
              <div class="screenshot-title" v-show="showTitle">{{ getCurrentCapturePhotoFrameTitle() }}</div>
              <div class="select-angle-btn" @click="!isSavingCameraPhoto && !showCameraOption && openFrameSelection()">
                SELECT 
                <br>
                ANGLE
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
    <custom-dialog
      :isDialogOpen="isDialogPhotoUploadIssueOpen"
      @close="closeDialog"
      @skip="closeDialog"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import draggable from "vuedraggable";
import PhotoUploadService from "@/services/photo-upload";
import mixin from "@/mixins/common";
import { promisePool } from "@/helper/promise-pool";
import { Fullscreen } from "@/helper/fullscreen";
import { 
  MAX_FILE_SIZE, 
  MAX_UPLOAD_FILE_SIZE, 
  EXTENSIONS, 
  AUTOFLIP_URL, 
  socialNetworkBrowser,
  browserList,
} from "@/helper/constant";
import heicConvert from "heic-convert";
import { logError, tiffToBlob, resizeImage } from "@/helper/utils";
import UploadPersistenceService from "@/helper/upload-persistence";


export default {
  name: "Index",
  mixins: [mixin],
  components: {
    CustomIcon: () => import("../../components/common/CustomIcon"),
    BtnGroup: () => import("../../components/common/BtnGroup.vue"),
    Tooltips: () => import("../../components/photo-upload/Tooltips.vue"),
    CustomDialog: () => import("../../components/common/CustomDialog"),
    Banner: () => import("../../components/photo-upload/Banner.vue"),
    PhotoUploadBox: () => import("../../components/photo-upload/PhotoUploadBox.vue"),
    draggable,
    ExitSurvey: () => import("../../components/common/ExitSurvey.vue"),
  },
  data() {
    return {
      uploadImage: [],
      newFiles: [],
      dragCounter: 0,
      max: MAX_FILE_SIZE,
      errImgSize: [],
      maxLength: 30,
      autoflipURL: AUTOFLIP_URL,
      errImgType: [],
      minLength: 1,
      onUpload: false,
      imgProcessText: "Processing...",
      showProcess: false,
      processAt: 0,
      processMaxWaitTime: 120000, // 2 min
      showCameraOption: false,
      curBodyType: "Sedan",
      curFrame: "",
      isAccessingCamera: false,
      isSavingCameraPhoto: false,
      cameraStream: null,
      cameraConstraints: {
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      },
      showImgSizeErr: false,
      showImgTypeErr: false,
      showMaxLengthErr: false,
      showHelpGuide: true,
      showBodyTypePanel: false,
      showFrameSelection: false,
      isClosing: false,
      checkingImg: {
        // exteriorCoverShot: false,
        exteriorFront: false,
        exteriorFrontSideL: false,
        exteriorSideL: false,
        exteriorRearSideL: false,
        exteriorRear: false,
        exteriorRearSideR: false,
        exteriorSideR: false,
        exteriorFrontSideR: false,
        exteriorTyre: false,
        exteriorRoof: false,
        interiorMeter: false,
        interiorDashboard: false,
        interiorSteeringWheel: false,
        interiorFrontSeat: false,
        interiorBackSeat: false,
        interiorThirdRowSeating: false,
        interiorBoot: false,
        interiorKey: false,
        interiorOwnerManual: false,
        interiorServiceHistory: false,
        interiorBuildPlate: false,
        interiorOther: false,
      },
      imageDeleting: false,
      showTitle: true,
      isLandscape: false,
      isMobile: true,
      isSpecialDeviceBrowser: false,
      isDialogPhotoUploadIssueOpen: false,
      showPhotoUploadBox: true,
      isNotUseAutoplay: false,
      isComponentActive: true,
    };
  },
  computed: {
    ...mapState({
      steps: (state) => state.common.steps,
      info: (state) => state.info.curVehicleInfo,
      bodyTypes: (state) => state.info.bodyTypes,
      stateImgs: (state) => state.photo.uploadImage,
      systemConfigurations: (state) => state.common.systemConfigurations,
    }),
    ...mapGetters({
      hasGotQuoteResponse: "hasGotQuoteResponse",
    }),
    notUploadItems: function () {
      return this.isMobile
        ? this.uploadImage.length < 6
          ? 6 - this.uploadImage.length
          : 0
        : this.uploadImage.length < 9
        ? 9 - this.uploadImage.length
        : 0;
    },
    currentCapturePhoto() {
      return this.curFrame;
    },
    currentUploadPhoto() {
      return this.photoOrdersFiltered[this.uploadImage.length] ?? {};
    },
    curBodyTypeFormat: function() {
      let string = this.curBodyType.toLowerCase();
      if (this.curBodyType === 'People mover/Van'){
        return string = 'Mover';
      } else {
        return string.charAt(0).toUpperCase() + string.slice(1);
      }
    },
    photoOrders() {
      return [
        // {
        //   frame: `Exterior/${this.curBodyType}/${this.curBodyType}_Front_Side_Left_Transparent.svg`,
        //   title: "COVER SHOT",
        //   type: "EXTERIOR",
        //   name: "exterior cover shot",
        //   key: "exteriorCoverShot",
        //   haveImg: this.checkingImg.exteriorCoverShot,
        // },
        {
          frame: `Exterior/${this.curBodyTypeFormat}/${this.curBodyTypeFormat}_Front.svg`,
          title: "FRONT",
          type: "EXTERIOR",
          name: "exterior front",
          key: "exteriorFront",
          haveImg: this.checkingImg.exteriorFront,
        },
        {
          frame: `Exterior/${this.curBodyTypeFormat}/${this.curBodyTypeFormat}_Front_Side_Left_Transparent.svg`,
          title: "3/4 FRONT (LEFT)",
          type: "EXTERIOR",
          name: "exterior front side l",
          key: "exteriorFrontSideL",
          haveImg: this.checkingImg.exteriorFrontSideL,
        },
        {
          frame: `Exterior/${this.curBodyTypeFormat}/${this.curBodyTypeFormat}_Side_Left.svg`,
          title: "SIDE (LEFT)",
          type: "EXTERIOR",
          name: "exterior side l",
          key: "exteriorSideL",
          haveImg: this.checkingImg.exteriorSideL,
        },
        {
          frame: `Exterior/${this.curBodyTypeFormat}/${this.curBodyTypeFormat}_Back_Side_Left.svg`,
          title: "3/4 REAR (LEFT)",
          type: "EXTERIOR",
          name: "exterior rear side l",
          key: "exteriorRearSideL",
          haveImg: this.checkingImg.exteriorRearSideL,
        },
        {
          frame: `Exterior/${this.curBodyTypeFormat}/${this.curBodyTypeFormat}_Back.svg`,
          title: "REAR",
          type: "EXTERIOR",
          name: "exterior rear",
          key: "exteriorRear",
          haveImg: this.checkingImg.exteriorRear,
        },
        {
          frame: `Exterior/${this.curBodyTypeFormat}/${this.curBodyTypeFormat}_Back_Side_Right.svg`,
          title: "3/4 REAR (RIGHT)",
          type: "EXTERIOR",
          name: "exterior rear side r",
          key: "exteriorRearSideR",
          haveImg: this.checkingImg.exteriorRearSideR,
        },
        {
          frame: `Exterior/${this.curBodyTypeFormat}/${this.curBodyTypeFormat}_Side_Right.svg`,
          title: "SIDE (RIGHT)",
          type: "EXTERIOR",
          name: "exterior side r",
          key: "exteriorSideR",
          haveImg: this.checkingImg.exteriorSideR,
        },
        {
          frame: `Exterior/${this.curBodyTypeFormat}/${this.curBodyTypeFormat}_Front_Side_Right_Transparent.svg`,
          title: "3/4 FRONT (RIGHT)",
          type: "EXTERIOR",
          name: "exterior front side r",
          key: "exteriorFrontSideR",
          haveImg: this.checkingImg.exteriorFrontSideR,
        },
        {
          frame: "Exterior/Car_tyre.svg",
          title: "CAR TYRE",
          type: "EXTERIOR",
          name: "exterior tyre",
          notSendToQuote: true,
          key: "exteriorTyre",
          haveImg: this.checkingImg.exteriorTyre,
        },
        {
          frame: "Exterior/Car_roof.svg",
          title: "CAR ROOF",
          type: "EXTERIOR",
          name: "exterior roof",
          notSendToQuote: true,
          key: "exteriorRoof",
          haveImg: this.checkingImg.exteriorRoof,
        },
        {
          frame: "Interior/ODOMETER.svg",
          title: "ODOMETER",
          type: "INTERIOR",
          name: "interior meter",
          notSendToQuote: true,
          key: "interiorMeter",
          haveImg: this.checkingImg.interiorMeter,
        },
        {
          frame: "Interior/DASHBOARD.svg",
          title: "DASHBOARD",
          type: "INTERIOR",
          name: "interior dash",
          notSendToQuote: true,
          key: "interiorDashboard",
          haveImg: this.checkingImg.interiorDashboard,
        },
        {
          frame: "Interior/Steering_wheel.svg",
          title: "STEERING WHEEL",
          type: "INTERIOR",
          name: "interior steering wheel",
          notSendToQuote: true,
          key: "interiorSteeringWheel",
          haveImg: this.checkingImg.interiorSteeringWheel,
        },
        {
          frame: "Interior/FRONT SEAT.svg",
          title: "FRONT SEAT",
          type: "INTERIOR",
          name: "interior front seat",
          notSendToQuote: true,
          key: "interiorFrontSeat",
          haveImg: this.checkingImg.interiorFrontSeat,
        },
        {
          frame: "Interior/BACK SEAT.svg",
          title: "BACK SEAT",
          type: "INTERIOR",
          name: "interior back seat",
          notSendToQuote: true,
          key: "interiorBackSeat",
          haveImg: this.checkingImg.interiorBackSeat,
        },
        {
          frame: "Interior/3RD ROW SEAT.svg",
          title: "3RD ROW SEAT",
          type: "INTERIOR",
          name: "interior third row seating",
          notSendToQuote: true,
          key: "interiorThirdRowSeating",
          haveImg: this.checkingImg.interiorThirdRowSeating,
        },
        {
          frame: `Interior/Boot - ${this.curBootFrame}.svg`,
          title: "BOOT",
          type: "INTERIOR",
          name: "boot",
          notSendToQuote: true,
          key: "interiorBoot",
          haveImg: this.checkingImg.interiorBoot,
        },
        {
          frame: "Interior/KEYS.svg",
          title: "KEYS",
          type: "INTERIOR",
          name: "key",
          notSendToQuote: true,
          key: "interiorKey",
          haveImg: this.checkingImg.interiorKey,
        },
        {
          frame: "",
          title: "OWNER'S MANUAL",
          type: "INTERIOR",
          name: "interior owner's manual",
          notSendToQuote: true,
          key: "interiorOwnerManual",
          haveImg: this.checkingImg.interiorOwnerManual,
        },
        {
          frame: "",
          title: "SERVICE HISTORY",
          type: "INTERIOR",
          name: "interior service history",
          notSendToQuote: true,
          key: "interiorServiceHistory",
          haveImg: this.checkingImg.interiorServiceHistory,
        },
        {
          frame: "Interior/BUILD PLATE.svg",
          title: "BUILD PLATE",
          type: "INTERIOR",
          name: "interior build plate",
          notSendToQuote: true,
          key: "interiorBuildPlate",
          haveImg: this.checkingImg.interiorBuildPlate,
        },
        {
          // frame: "Interior/Other.svg",
          frame: "",
          title: "OTHER",
          type: "INTERIOR",
          name: "interior other",
          notSendToQuote: true,
          key: "interiorOther",
          haveImg: this.checkingImg.interiorOther,
        },
      ]
    },
    bodyTypes() {
      return ['Sedan', 'SUV', 'Hatchback', 'Ute', 'Wagon', 'Coupe', 'Convertible', 'People mover/Van'];
    },
    photoOrdersFiltered() {
      const filteredType = ['SUV', 'People mover/Van'];
      let tempFilteredList = [];
      if(!filteredType.includes(this.curBodyType)) {
        this.photoOrders.forEach((photo) => {
          photo.key !== 'interiorThirdRowSeating' && tempFilteredList.push(photo);
        })
      } else {
        tempFilteredList = [...this.photoOrders];
      }
      return tempFilteredList;
    },
    curBootFrame() {
      let curBootSVGType = '';
      if (this.curBodyType === 'Sedan' || this.curBodyType === 'Convertible') {
        curBootSVGType = 'Sedan';
      } else {
        curBootSVGType = 'SUV NEW';
      }
      return curBootSVGType || "";
    },
    mobileMediaQuery: function() {
      return window.matchMedia("(min-width: 900px)");
    },
    showNudge() {
      return this.uploadImage.length === 0 || this.uploadImage.length >= 10
    },
    isUploadedImages() {
      return this.uploadImage.length > 0;
    },
    showFeedbackMessage() {
      return this.uploadImage.length >= 1 && !this.showNudge
    },
    photosNeeded() {
      return 10 - this.uploadImage.length;
    },
    photosUploadPercent() {
      return this.photosNeeded < 6 ? "33%" : "98%";
    },
    btnGroupPadding() {
      if (!this.isMobile) {
        if (this.uploadImage.length === 0) return 'custom-padding-3';
        else if (this.uploadImage.length <= 10) return 'custom-padding-2';
        else return '';
      }
      return '';
    },
    isHavingUploadIssue() {
      return this.uploadImage.every(image => Object.hasOwnProperty.call(image, "err"));
    },
    maxIndexItem() {
      return this.uploadImage
        .filter((image) => !image.onUpload && !image.err)
        .reduce((maxItem, currentItem) => {
          const maxIndex = maxItem ? parseInt(
            maxItem?.filename?.match(/(\d+)\.jpg$/)?.[1],
            10
          ) : -1;
          const currentIndex = parseInt(
            currentItem?.filename?.match(/(\d+)\.jpg$/)?.[1],
            10
          );
          return currentIndex > maxIndex ? currentItem : maxItem;
        }, null);
    },
    dragEnabled() {
      return this.isNotUseAutoplay;
    },
  },
  methods: {
    ...mapActions({
      evaluateImages: "evaluateImages",
      pollQuoteResponse: "pollQuoteResponse",
    }),
    useThisPhoto() {
      this.isSavingCameraPhoto = true;
      const output = this.$refs.cameraOutput;
      const context = output.getContext("2d");

      // Create temp canvas from canvas
      const tempCanvas = document.createElement("canvas");
      const tempContext = tempCanvas.getContext("2d");
      tempCanvas.width = output.width;
      tempCanvas.height = output.height;
      tempContext.drawImage(output, 0, 0);

      if (output.height > output.width) {
        const tempWidth = output.width;
        output.width = output.height;
        output.height = tempWidth;
        context.translate(output.width / 2, output.height / 2);
        context.rotate((-90 * Math.PI) / 180);
        context.drawImage(
          tempCanvas,
          -output.height / 2,
          -output.width / 2,
          output.height,
          output.width
        );
        context.resetTransform();
      } else {
        context.drawImage(tempCanvas, 0, 0, output.width, output.height);
      }

      output.toBlob(
        async (blob) => {
          const imgId = this.$uuid.v4();
          this.uploadImage = [
            ...this.uploadImage,
            {
              id: imgId,
              filename: this.currentCapturePhoto.name,
              name: this.currentCapturePhoto.name,
              title: this.currentCapturePhoto.title,
              type: this.currentCapturePhoto.type,
              key: this.currentCapturePhoto.key,
              notSendToQuote: this.currentCapturePhoto.notSendToQuote,
              url: null,
              onUpload: true,
              progress: 0,
              fileData: blob, // Store file data for resume
            },
          ];
          this.handleImgUpload(blob, imgId);
          this.isSavingCameraPhoto = false;
          this.showCameraOption = false;
          this.checkingImg[this.currentCapturePhoto.key] = true;
          this.nextFrame();
          this.clearVideoOutput();
          if (this.uploadImage.length === this.maxLength) {
            this.$bvModal.hide("camera-modal");
            this.cameraStream.getTracks().forEach((track) => track.stop());
          }
        },
        "image/jpeg",
        1
      );

      this.$store.dispatch("setTakePhotoWithGuidedCamera", true);
    },
    takePhotoAgain() {
      this.clearVideoOutput();
      this.showCameraOption = false;
    },
    takePhoto() {
      this.showBodyTypePanel = false;
      this.showHelpGuide = false;
      this.showFrameSelection = false;
      const { w, h } = this.getVideoSize(this.$refs.cameraView);
      this.$refs.cameraOutput.width = w;
      this.$refs.cameraOutput.height = h;
      this.$refs.cameraOutput
        .getContext("2d")
        .drawImage(this.$refs.cameraView, 0, 0);
      this.showCameraOption = true;
    },
    exitCamera() {
      this.$bvModal.hide("camera-modal");
      this.clearVideoOutput();
      this.cameraStream.getTracks().forEach((track) => track.stop());
      this.toggleChatSection();
      this.closeDropdown();
    },
    async initImgUploadCamera() {
      try {
        this.isAccessingCamera = true;
        this.cameraStream = await navigator.mediaDevices.getUserMedia(
          this.cameraConstraints
        );
        this.isAccessingCamera = false;
        this.$bvModal.show("camera-modal");
        this.$nextTick(() => {
          new Fullscreen(document.querySelector("#camera-modal")).toggle();
          this.$refs.cameraView.srcObject = this.cameraStream;
        });
        const chatSection = document.getElementsByTagName('mmgp-sms-chat-widget')[0];
        if (chatSection) {
          chatSection.style.display = "none";
        }
        const hubspotChatbox = document.getElementById('hubspot-messages-iframe-container');
        if (hubspotChatbox) {
          hubspotChatbox.style.setProperty('display', 'none', 'important');
        }
      } catch (err) {
        this.isAccessingCamera = false;
        this.$toast.error("Unable to access camera");
        logError(`Unable to access camera: ${err}`);
      }
    },
    getVideoSize(ele) {
      const w = ele.videoWidth;
      const h = ele.videoHeight;
      return { w, h };
    },
    clearVideoOutput() {
      this.$refs.cameraOutput
        .getContext("2d")
        .clearRect(
          0,
          0,
          this.$refs.cameraOutput.width,
          this.$refs.cameraOutput.height
        );
    },
    goBack() {
      this.$store.dispatch("setUploadedImages", this.uploadImage);
      this.$router.push({ name: "vehicle-info", query: this.$route.query });
    },
    async next() {
      this.$store.dispatch("setProgress", 96);
      this.$store.dispatch("setUploadedImages", this.uploadImage);
      this.$store.dispatch("setSendPhotoLater", false);
      this.$router.push({ name: "customer-detail", query: this.$route.query });
    },
    goNext() {
      this.$store.dispatch("setProgress", 96);
      if (!this.onUpload) {
        this.$store.dispatch("setUploadedImages", this.uploadImage);
        this.$store.dispatch("clearSendPhotoLater");
        this.$router.push({ name: "customer-detail", query: this.$route.query });
      }
    },
    async rotate(i) {
      this.onUpload = true;
      this.uploadImage = this.uploadImage.map((item) => {
        if (item.id !== i.id) return item;
        else {
          return {
            ...item,
            onUpload: true,
          };
        }
      });
      try {
        const newImg = await PhotoUploadService.rotate(i);
        this.uploadImage = this.uploadImage.map((item) => {
          if (item.id !== i.id) return item;
          else {
            return {
              ...item,
              ...newImg,
              id: i.id,
              onUpload: false,
            };
          }
        });
        this.$store.dispatch("setUploadedImages", this.uploadImage);
      } catch (err) {
        // Distinguish network issue vs other server errors
        const isNetworkError = !err.response;
        if (this.$toast && this.$toast.error) {
          if (isNetworkError) {
            this.$toast.error("Network issue: The photo was NOT rotated. Please try again once your internet connection is stable.");
          } else {
            this.$toast.error("Too many requests");
          }
        }
        logError(`Rotate image error: ${err}`);
        // Reset per-image loading state on failure
        this.uploadImage = this.uploadImage.map((item) => {
          if (item.id !== i.id) return item;
          return {
            ...item,
            onUpload: false,
          };
        });
      }
      this.onUpload = false;
    },
    convertImageMimeType: async function(imageFile, predominantColorsDetected, checkers) {
      try {
        checkers.push(() =>
          this.checkPredominantColors(
            imageFile,
            "user's uploaded file",
            predominantColorsDetected
          )
        );
        const fileExt = (imageFile.name || "").split(".").pop();
        if (/(tiff|tif)/gi.test(fileExt)) {
          const tiffBlob = await tiffToBlob(imageFile);
          checkers.push(() =>
            this.checkPredominantColors(
              tiffBlob,
              "tiff|tif converter",
              predominantColorsDetected
            )
          );
          return tiffBlob;
        } else if (/(heic)/gi.test(fileExt)) {
          const blob = await imageFile.arrayBuffer();
          const buffer = Buffer.from(blob);
          const jpgBuffer = await heicConvert({
            buffer,
            format: "JPEG",
            quality: 1,
          });
          const outputFile = new File([jpgBuffer], "output.jpg", {
            type: "image/jpeg",
          });
          checkers.push(() =>
            this.checkPredominantColors(
              outputFile,
              "heic converter",
              predominantColorsDetected
            )
          );
          return outputFile;
        }
        return imageFile;
      } catch (err) {
        logError(`convertImageMimeType error: ${err.message}`);
        console.log(err);
        console.log(err.error);
        return imageFile;
      }
    },
    handleImgUpload: async function(file, imgId) {
      try {
        let img;
        let predominantColorsDetected = {};
        const checkers = [];
        const imageFile = await this.convertImageMimeType(
          file,
          predominantColorsDetected,
          checkers
        );
        if (imageFile.size > MAX_UPLOAD_FILE_SIZE) {
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            maxIteration: 20,
            fileType: "image/jpeg",
          };
          img = await resizeImage(imageFile, options);
          checkers.push(() =>
            this.checkPredominantColors(
              img,
              "image resizer",
              predominantColorsDetected
            )
          );
        } else {
          img = imageFile;
        }
        const blobToUrl = URL.createObjectURL(img);
        this.uploadImage = this.uploadImage.map((stateImge) =>
          stateImge.id === imgId
            ? {
                ...stateImge,
                url: blobToUrl,
              }
            : stateImge
        );
        let formData = new FormData();
        formData.append("image", img);
        formData.append(
          "predominantColorsDetected",
          Object.values(predominantColorsDetected).includes(true)
        );
        formData.append("baseStoragePath", `AU/LP/${this.info.recordId}`);
        formData.append("photoOrder", this.getPhotoOrder(imgId));

        // Save file data for potential resume after refresh
        const fileData = img instanceof File ? img : new File([img], 'image.jpg', { type: img.type || 'image/jpeg' });

        // Save file data to IndexedDB immediately for potential resume (only if not already exists)
        const existingFileData = await UploadPersistenceService.getFileData(imgId);
        if (!existingFileData) {
          await UploadPersistenceService.saveFileData(imgId, fileData);
        }

        const uploadedImgData = await PhotoUploadService.uploadImage(
          formData,
          imgId,
          (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            this.uploadImage = this.uploadImage.map((stateImge) =>
              stateImge.id === imgId
                ? {
                    ...stateImge,
                    progress,
                  }
                : stateImge
            );
          }
        );
        URL.revokeObjectURL(blobToUrl);
        if (
          uploadedImgData?.isPredominantColorsImage &&
          !Object.values(predominantColorsDetected).includes(true)
        ) {
          checkers.push(() =>
            this.logPredominantColorsError(
              "uploaded image in S3",
              uploadedImgData
            )
          );
        }
        this.runCheckersSequentially(checkers);
        this.uploadImage = this.uploadImage
          .filter((img) => {
            return !Object.hasOwnProperty.call(uploadedImgData, "err")
              ? !Object.hasOwnProperty.call(img, "err")
              : img;
          })
          .map((exImg) => {
            return imgId !== exImg.id
              ? exImg
              : {
                  ...exImg,
                  ...uploadedImgData,
                  onUpload: uploadedImgData.isUploadInterrupted ? true : false,
                  progress: uploadedImgData.isUploadInterrupted ? 0 : 100,
                  fileData: fileData, // Store file data for resume
                };
          });
        const stillUploading = this.uploadImage.some(img => img.onUpload || (img.progress && img.progress < 100));
        if (!stillUploading) {
          this.onUpload = false;
        }
        this.$store.dispatch("setUploadedImages", this.uploadImage);
        // Clean up file data from IndexedDB after successful upload
        if (this.isComponentActive && !uploadedImgData.isUploadInterrupted) {
          await UploadPersistenceService.deleteFileData(imgId);
        }
      } catch (err) {
        logError(`handleImgUpload error: ${err}`);
      }
    },
    uploadPhoto: async function (event) {
      var input = event.target;
      const promiseBatch = [];
      this.errImgSize = [];
      this.errImgType = [];
      this.showMaxLengthErr = false;
      this.showImgSizeErr = false;
      this.showImgTypeErr = false;
      this.showPhotoUploadBox = this.uploadImage.length + input.files.length < this.maxLength;
      var count_file = this.uploadImage.length;
      if (input.files && this.uploadImage.length < this.maxLength) {
        this.onUpload = true;
        // Create an array to store promises for saving file data
        const saveFilePromises = [];

        Array.from(input.files).forEach((file) => {
          if (!EXTENSIONS.includes(file.name.split('.').pop().toLowerCase())) {
            this.errImgType = [...this.errImgType, file.name];
          } else if (file.size > this.max) {
            this.errImgSize = [...this.errImgSize, file.name];
          } else if (this.uploadImage.length < this.maxLength) {
            const imgId = this.$uuid.v4();
            this.nextFrame();
            this.checkingImg[this.curFrame.key] = true;
            this.uploadImage = [
              ...this.uploadImage,
              {
                id: imgId,
                filename: file.name,
                url: null,
                name: file.name,
                key: this.photoOrdersFiltered[this.uploadImage.length]?.key,
                title: this.photoOrdersFiltered[this.uploadImage.length]?.title,
                type: this.photoOrders[this.uploadImage.length]?.type,
                onUpload: true,
                progress: 0,
                fileData: file, // Store file data for resume
              },
            ];
            // Save images to IndexedDB
            saveFilePromises.push(UploadPersistenceService.saveFileData(imgId, file));

            promiseBatch.push(() => this.handleImgUpload(file, imgId));
          }
          count_file = count_file + 1 ;
        });

        // Wait for all file data to be saved before continuing
        await Promise.all(saveFilePromises);
        if (this.errImgSize.length > 0) {
          this.showPhotoUploadBox = true;
          this.showImgSizeErr = true
        } else {
          this.showImgSizeErr = false
        }
        if (this.errImgType.length > 0) {
          this.showPhotoUploadBox = true;
          this.showImgTypeErr = true
        } else {
          this.showImgTypeErr = false
        }
        await promisePool(promiseBatch, 3);
        this.onUpload = false;
        event.target.value = "";
      }
      if (count_file > this.maxLength) {
        event.target.value = "";
        this.showMaxLengthErr = true
      }
    },
    drop: async function(e) {
      this.doNothing(e);
      this.showPhotoUploadBox = this.uploadImage.length + e.dataTransfer.items.length < this.maxLength;
      if (this.uploadImage.length === this.maxLength) {
        this.showMaxLengthErr = true;
        return;
      }
      const uploadJob = [];
      var count_file = this.uploadImage.length;
      const handleUploadFile = (img) => {
        if (!EXTENSIONS.includes(img.name.split(".").pop().toLowerCase())) {
          this.errImgType = [...this.errImgType, img.name];
        } else if (img.size > this.max) {
          this.errImgSize = [...this.errImgSize, img.name];
        } else if (this.uploadImage.length < this.maxLength) {
          const imgId = this.$uuid.v4();
          this.uploadImage = [
            ...this.uploadImage,
            {
              id: imgId,
              name: img.name,
              title: this.currentPhoto?.title,
              url: null,
              onUpload: true,
              progress: 0,
            },
          ];
          uploadJob.push(() => this.handleImgUpload(img, imgId));
        }
      };
      async function getFile(fileEntry) {
        try {
          return new Promise((resolve, reject) =>
            fileEntry.file(resolve, reject)
          );
        } catch (err) {
          logError(`Get file error: ${err}`);
        }
      }
      function traverse_directory(entry) {
        let reader = entry.createReader();
        return new Promise((resolve_directory) => {
          let iteration_attempts = [];
          (function read_entries() {
            reader.readEntries((entries) => {
              if (!entries.length) {
                resolve_directory(Promise.all(iteration_attempts));
              } else {
                iteration_attempts.push(
                  entries.map(async (entry) => {
                    if (entry.isFile) {
                      const file = await getFile(entry);
                      handleUploadFile(file);
                      return file;
                    } else {
                      return traverse_directory(entry);
                    }
                  })
                );
                read_entries();
              }
            });
          })();
        });
      }
      // upload folder
      if (
        this.uploadImage.length < this.maxLength &&
        e.dataTransfer.items.length > 0
      ) {
        this.onUpload = true;
        this.errImgType = [];
        this.errImgSize = [];
        this.showImgSizeErr = false;
        this.showImgTypeErr = false;
        Array.from(e.dataTransfer.items).forEach(async (item, idx) => {
          const entry = item.webkitGetAsEntry();
          if (entry != null) {
            if (entry.isDirectory) {
              traverse_directory(entry).then(async () => {
                await promisePool(uploadJob, 3);
                e.target.value = "";
                if (this.errImgSize.length > 0) {
                  this.showPhotoUploadBox = true;
                  this.showImgSizeErr = true;
                } else {
                  this.showImgSizeErr = false;
                }
                if (this.errImgType.length > 0) {
                  this.showPhotoUploadBox = true;
                  this.showImgTypeErr = true;
                } else {
                  this.showImgTypeErr = false;
                }
              });
            }
            if (entry.isFile) {
              handleUploadFile(e.dataTransfer.files[idx]);
              count_file = count_file + 1;
            }
          }
        });
        await promisePool(uploadJob, 3);
        this.onUpload = false;
        e.target.value = "";
        if (this.errImgSize.length > 0) {
          this.showPhotoUploadBox = true;
          this.showImgSizeErr = true;
        } else {
          this.showImgSizeErr = false;
        }
        if (this.errImgType.length > 0) {
          this.showPhotoUploadBox = true;
          this.showImgTypeErr = true;
        } else {
          this.showImgTypeErr = false;
        }
      } else if (e.dataTransfer.items.length > 0) {
        this.showPhotoUploadBox = true;
        this.showMaxLengthErr = true;
        e.target.value = "";
      }
      if (count_file > this.maxLength) {
        this.showMaxLengthErr = true;
        e.target.value = "";
      }
    },
    doNothing(event) {
      event.stopPropagation();
      event.preventDefault();
    },
    async deleteImg(filename, key, id) {
      if (!this.showPhotoUploadBox) this.showPhotoUploadBox = true;
      if (this.showMaxLengthErr) this.showMaxLengthErr = false;
      // Mark global and item loading state
      this.onUpload = true;
      this.uploadImage = this.uploadImage.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          onUpload: true,
        };
      });
      
      try {
        // Call API to delete image
        await PhotoUploadService.deleteImg(filename);
        // Remove image from list only on success
        this.uploadImage = this.uploadImage.filter(
          (item) => item.filename !== filename
        );
        this.imageDeleting = true;
        setTimeout(() => (this.imageDeleting = false), 300);
        this.checkingImg[key] = false;
        if (this.uploadImage.length === 1 || key === "exteriorFront") {
          this.curFrame = this.photoOrdersFiltered[0];
        }
        // Clean up file data from IndexedDB only when delete succeeds
        await UploadPersistenceService.deleteFileData(id);
        this.$store.dispatch("setUploadedImages", this.uploadImage);
      } catch (e) {
        // Handle network / server error so UI doesn't get stuck in loading state
        logError(`Delete image error: ${e}`);
        if (this.$toast && this.$toast.error) {
          this.$toast.error("Network issue: The photo was NOT deleted. Please try again once your internet connection is stable.");
        }
        // Reset per-image loading state on failure
        this.uploadImage = this.uploadImage.map((item) => {
          if (item.id !== id) return item;
          return {
            ...item,
            onUpload: false,
          };
        });
      } finally {
        // Always clear global loading flag
        this.onUpload = false;
      }
    },
    async deleteAllImages() {
      Promise.all(
        this.uploadImage.map((img) =>
          PhotoUploadService.deleteImg(img.filename)
        )
      );
      for (const [key] of Object.entries(this.checkingImg)) {
        this.checkingImg[key] = false;
      }
      this.uploadImage = [];
      this.curFrame = this.photoOrdersFiltered[0];
      this.$store.dispatch("setProgress", 0);
      this.$store.dispatch("clearImages");
      // Clear all file data from IndexedDB
      await UploadPersistenceService.clearAllFileData();
      this.$store.dispatch("setUploadedImages", this.uploadImage);
    },
    openHelpGuide() {
      this.showFrameSelection = false;
      this.showBodyTypePanel = false;
      this.showHelpGuide = !this.showHelpGuide;
    },
    toggleBodyTypePanel() {
      this.showHelpGuide = false;
      this.showFrameSelection = false;
      this.showBodyTypePanel = !this.showBodyTypePanel;
    },
    updateBodyType(bodyType) {
      this.curBodyType = bodyType;
      this.showBodyTypePanel = false;
      // Update current Frame
      this.nextFrame();
    },
    closeDropdown() {
      this.showBodyTypePanel = false;
      this.showFrameSelection = false;
      this.showHelpGuide = false;
    },
    openFrameSelection () {
      this.showBodyTypePanel = false;
      this.showFrameSelection = !this.showFrameSelection;
      this.showHelpGuide = false;
    },
    enableExitConfirmDialog() {
      this.isClosing = true;
    },
    closeCamera() {
      this.exitCamera();
      this.showCameraOption = false;
      this.isClosing = false;
      this.toggleChatSection();
    },
    saveAndExit() {
      this.useThisPhoto();
      this.closeCamera();
    },
    discardAndExit() {
      this.deleteAllImages();
      this.closeCamera();
    },
    setCurrentPhoto(index) {
      this.curFrame = this.photoOrdersFiltered[index];
      this.showTitle = false;
      if(this.curBodyType == "SUV" && this.curFrame.key == "interiorBoot"){
       this.curFrame.frame  = "Interior/Boot - SUV NEW.svg";
      }
      this.showTitle = false;
      this.$nextTick(() => {
        this.showTitle = true;
      });
      this.showFrameSelection = false;
    },
    nextFrame() {
      this.showTitle = false;
      if(this.uploadImage.length >= this.photoOrdersFiltered.length) {
        this.curFrame = this.photoOrdersFiltered[this.photoOrdersFiltered.length - 1];
      } else {
        for (let photoFrame of this.photoOrdersFiltered) {
          if (photoFrame.haveImg === false) {
            this.curFrame = photoFrame;
            break;
          }
        }
      }
      this.showTitle = false;
      this.$nextTick(() => {
        this.showTitle = true;
      });
    },
    getCurrentCapturePhotoFrameTitle() {
      const title = this.curFrame.title;
      if (title) {
        return title.toUpperCase();
      }
    },
    getChatSection() {
      return document.getElementsByTagName('mmgp-sms-chat-widget')[0];
    },
    toggleChatSection() {
      const chatSection = this.getChatSection();
      if (chatSection) {
        if (chatSection.style.display === 'none') {
          chatSection.style.display = "block";
        } else {
          chatSection.style.display = "none";
        }
      }
    },
    openDialog() {
      this.isDialogPhotoUploadIssueOpen = true;
    },
    closeDialog() {
      this.isDialogPhotoUploadIssueOpen = false;
    },
    async checkPredominantColors(imageBuffer, action, predominantColorsDetected) {
      try {
        if (!["image/jpg", "image/jpeg", "image/png", "image/tiff"].includes(imageBuffer.type)) {
          return false;
        }
        let formData = new FormData();
        formData.append("image", imageBuffer);
        const checkResult = await PhotoUploadService.detectPredominantColors(formData);
        const isLoggedError = Object.values(predominantColorsDetected).includes(true);
        if (checkResult && !isLoggedError) {
          predominantColorsDetected[action] = true;
          const imageDetails = {
            type: imageBuffer.type,
            size: imageBuffer.size,
          }
          this.logPredominantColorsError(action, imageDetails);
        }
        return checkResult;
      } catch (err) {
        logError(`Check predominant colors error: ${err}`);
        return false;
      }
    },
    logPredominantColorsError(action, imageDetails = {}) {
      const hint = {
        imageDetails,
        rego: this.info.rego,
        state: this.info.location,
      };
      logError(`Predominant colour is detected from "${action}". ${JSON.stringify(hint)}`);
    },
    runCheckersSequentially(checkers) {
      let currentCheckerIndex = 0;
      const runNextChecker = () => {
        if (currentCheckerIndex < checkers.length) {
          // Run the current checker and move to the next one only if it returns false
          checkers[currentCheckerIndex]().then(result => {
            if (result === true) {
              return; // Stop execution of remaining checkers
            }
            currentCheckerIndex++;
            runNextChecker(); // Run the next checker
          });
        }
      };
      runNextChecker(); // Start the sequence
    },
    getPhotoOrder(id) {
      const maxItem = this.maxIndexItem;
      const currentIndex = this.uploadImage.findIndex(
        (image) => image.id === id
      );

      // If the item is not found, return 0 or another default value
      if (currentIndex === -1) return 1;
      // Compute the order from the index (1-based)
      const orderFromId = currentIndex + 1;

      // If there is no maxItem, return the computed order
      if (!maxItem) return orderFromId;
      const maxItemIndex = parseInt(
        maxItem.filename.match(/(\d+)\.jpg$/)[1],
        10
      );
      // Calculate the gap and return the updated order
      const gap =
        currentIndex -
        this.uploadImage.findIndex((image) => image.id === maxItem.id);

      return maxItemIndex + gap;
    },
    handleBeforeUnload() {
      this.$store.dispatch(
        "setUploadedImages",
        this.uploadImage
      );

      // Save upload state to localStorage for resume after refresh
      this.saveUploadStateToStorage();
    },
    // Handle for refresh page on iOS
    handlePageHide() {
      this.$store.dispatch(
        "setUploadedImages",
        this.uploadImage
      );
      // Save upload state to localStorage for resume after refresh
      this.saveUploadStateToStorage();
    },
    onReorderEnd() {
      this.$store.dispatch(
        "setUploadedImages",
        this.uploadImage.filter((image) => !image.onUpload)
      );
    },
    saveAndExitWithPhoto() {
      this.useThisPhoto();
      this.exitCamera();
    },
    // Methods for persistent upload state
    async saveUploadStateToStorage() {
      // Create a copy of uploadImage without fileData
      const uploadImageCopy = this.uploadImage.map(img => {
        // eslint-disable-next-line no-unused-vars
        const { fileData, ...imgWithoutFileData } = img;
        return imgWithoutFileData;
      });
      
      const uploadState = {
        uploadImage: uploadImageCopy,
        onUpload: this.onUpload,
        recordId: this.info.recordId,
        timestamp: Date.now()
      };
      localStorage.setItem('photoUploadState', JSON.stringify(uploadState));
      
      // Save file data to IndexedDB for images that are uploading
      const uploadingImages = this.uploadImage.filter(img => img.onUpload || (img.progress && img.progress < 100));
      for (const img of uploadingImages) {
        if (img.fileData) {
          await UploadPersistenceService.saveFileData(img.id, img.fileData);
        }
      }
    },
    getUploadStateFromStorage() {
      try {
        const savedState = localStorage.getItem('photoUploadState');
        if (savedState) {
          const uploadState = JSON.parse(savedState);
          // Check if the state is not too old (within 1 hour)
          const isRecent = Date.now() - uploadState.timestamp < 3600000;
          if (isRecent && uploadState.recordId === this.info.recordId) {
            return uploadState;
          }
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    async clearUploadStateFromStorage() {
      localStorage.removeItem('photoUploadState');
      // Clear all file data from IndexedDB
      await UploadPersistenceService.clearAllFileData();
    },
    async checkAndResumePendingUploads() {
      const savedState = this.getUploadStateFromStorage();
      if (savedState && savedState.uploadImage && savedState.uploadImage.length > 0) {
        // Check if there are any images that were uploading when page was refreshed
        const pendingUploads = savedState.uploadImage.filter(img => img.onUpload || (img.progress && img.progress < 100));
        if (pendingUploads.length > 0) {
          // Set onUpload to true to disable buttons during resume
          this.onUpload = true;
          
          // Restore the upload state
          this.uploadImage = savedState.uploadImage;
          this.$store.dispatch("setUploadedImages", this.uploadImage);
          
          // Resume uploads for images that were in progress
          for (const img of pendingUploads) {
            if (img.onUpload || (img.progress && img.progress < 100)) {
              try {
                // Try to get file data from IndexedDB
                const fileData = await UploadPersistenceService.getFileData(img.id);
                
                if (fileData) {
                  // Mark as uploading and retry upload
                  this.uploadImage = this.uploadImage.map(item => 
                    item.id === img.id ? { ...item, onUpload: true, progress: 0 } : item
                  );
                  
                  try {
                    await this.handleImgUpload(fileData, img.id);
                    // Clean up file data from IndexedDB after successful upload
                    await UploadPersistenceService.deleteFileData(img.id);
                  } catch (error) {
                     // Mark as failed
                    this.uploadImage = this.uploadImage.map(item => 
                      item.id === img.id ? { ...item, onUpload: false, err: true } : item
                    );
                  }
                } else {
                  // No file data available, mark as failed
                  this.uploadImage = this.uploadImage.map(item => 
                    item.id === img.id ? { ...item, onUpload: false, err: true } : item
                  );
                }
              } catch (error) {
                 // Mark as failed
                this.uploadImage = this.uploadImage.map(item => 
                  item.id === img.id ? { ...item, onUpload: false, err: true } : item
                );
              }
            }
          }
          
          // Check if there are any images still uploading after resume
          const stillUploading = this.uploadImage.some(img => img.onUpload || (img.progress && img.progress < 100));
          
          // Only set onUpload to false if no images are still uploading
          if (!stillUploading) {
            this.onUpload = false;
          }
          
          // Clear the saved state after processing
          this.clearUploadStateFromStorage();
        }
      }
    },
  },
  mounted() {
    this.isNotUseAutoplay = this.systemConfigurations.s3OnlyMode || !this.systemConfigurations.sendImagesToAutoplay;
    if (this.isNotUseAutoplay) {
      this.maxLength = Infinity;
    }
    this.showPhotoUploadBox = this.stateImgs.length < 30 || this.isNotUseAutoplay;
    this.uploadImage = this.stateImgs;
    this.curBodyType = this.bodyTypes.some(bodyType => bodyType.toLowerCase() === this.info.bodyType.toLowerCase()) ? this.info.bodyType : 'Sedan';
    this.curFrame = this.photoOrdersFiltered[0];

    // Check for pending uploads after page refresh
    this.checkAndResumePendingUploads();

    // Check mobile screen
    this.mobileMediaQuery.addListener((e) => {
      if (e.matches) {
        this.isMobile = false;
      } else {
        this.isMobile = true;
      }
    });

    this.isMobile = !this.mobileMediaQuery.matches;

    if (this.isMobile) {
      const userAgent = navigator.userAgent || window.opera; // Check user use in-app facebook or instagram browser android so can only upload
      const isAndroidSpecialBrowser = userAgent.includes('Android') && socialNetworkBrowser.some(browser => userAgent.includes(browser));
      const isiOSChromeBrowser = (userAgent.includes('iPhone') || userAgent.includes('iPad')) && userAgent.includes(browserList);

      if (isAndroidSpecialBrowser) {
        this.isSpecialDeviceBrowser = true;
        this.isDialogPhotoUploadIssueOpen = true;
      } else if (isiOSChromeBrowser) {
        this.isSpecialDeviceBrowser = true;
      }
    }

    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('pagehide', this.handlePageHide);
    window.onpopstate = null;
  },
  beforeDestroy() {
    this.isComponentActive = false;
    // Clean up the event listener when the component is destroyed
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('pagehide', this.handlePageHide);
    // Save state before component is destroyed
    this.saveUploadStateToStorage();
  },
  beforeRouteLeave(to, from, next) {
    (async () => {
      this.$store.dispatch("setUploadedImages", this.uploadImage);
      this.saveUploadStateToStorage();
      next();
    })();
  },
};
</script>

<style lang="scss">
.photo-description {
  font-size: 16px !important;
  text-align: left;
  font-family: "Avenir Next LT W05 Regular" !important;
  .photo-description-link {
    color: var(--photo-tips-text-link);
  }
}
///
.camera-modal {
  .modal-dialog {
    max-width: 100%;
    margin: 0;
    top: 0;
    left: 0;
    height: 100%;
    display: flex;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background-color: rgb(51, 51, 51);
  }
  .modal-content {
    width: 100%;
    height: 100%;
    background-color: initial;
  }
  .modal-body {
    width: 100%;
    height: 100%;
    padding: 0;
  }
  .camera-screen {
    background: white;
    position: relative;
    width: 100%;
    height: 100%;
    .function-section {
      height: 90px;
      width: 100%;
      background: rgba(37, 43, 47, 0.5);
      backdrop-filter: blur(22px);
      z-index: 1700;
      position: fixed;
      bottom: 0;
      left: 0;

      .shoot-btn {
        width: 100px;
        height: 100px;
        position: fixed;
        bottom: 45px;
        left: 50%;
        background: rgba(37, 43, 47, 0.5);
        -webkit-backdrop-filter: blur(22px);
        backdrop-filter: blur(22px);
        border-radius: 50%;
        transform: rotate(90deg) translate(5px, 40px);

        ::before {
          content: '';
          width: 2px;
          height: 50px;
          background: #ffffff;
          position: absolute;
          bottom: 100%;
          left: 50%;
          margin-left: -2px;
        }
        ::after {
          content: '';
          border: 1px solid #ffffff;
          width: 1px;
          height: 1000px;
          background: #ffffff;
        }
      }

      .button-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 100%;
        // padding: 0 20px;

        .upper-btn {
          .btn {
            margin: 10px;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .close-btn {
            border-radius: 50% !important;
            border: 2px solid #ffffff;
            background: rgba(255, 255, 255, 0.25);
            width: 50px;
            height: 50px;
            color: #ffffff;
            transform: rotate(90deg);
          }
        }

        .under-btn {
          display: flex;
          flex-direction: row-reverse;
          .btn {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .btn:first-child {
            margin: 10px 0px;
          }
          .btn:last-child {
            margin: 10px;
          }
          .body-type-btn, .help-btn {
            border-radius: 50% !important;
            border: 2px solid #ffffff;
            background: rgba(255, 255, 255, 0.25);
            width: 50px;
            height: 50px;
            transform: rotate(90deg);
          }
          .help-btn {
            color: #ffffff;
            font-size: 32px;
            &.active {
              background: #2BB673;
            }
          }
        }
      }
    }
    #camera-output {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1058;
      object-fit: cover;
    }
    #camera-outlay {
      user-select: none;
      pointer-events: none;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: calc(100% - 90px);
      z-index: 1057;
      display: flex;
      justify-content: center;
      align-items: center;
      img {
        width: calc(100vh - 120px);
        height: 45%;
        transform: rotate(90deg);
        padding-right: 90px;
        padding-left: 50px;
      }
    }
    .camera-outlay-buildplate {
      img {
        padding-right: 15px !important;
        padding-left: 30px !important;
      }
    }
    .camera-view {
      pointer-events: none;
      user-select: none;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1056;
      object-fit: cover;
    }
    .body-type-dropdown {
      z-index: 1700;
      position: fixed;
      transform: rotate(90deg) translateX(calc(-50% - 127px));
      top: 50%;
      left: 50px;
      color: #FFFFFF;
      ul {
        background: rgba(37, 43, 47, 0.5);
        border: 2px solid #FFFFFF;
        -webkit-backdrop-filter: blur(19px);
        backdrop-filter: blur(19px);
        border-radius: 22px;
        list-style-type: none;
        padding: 0 !important;
        max-height: 300px;
        overflow-y: auto;
        li {
          background: transparent;
          border-bottom: 1px solid #ffffff;
          height: 40px;
          padding: 10px 20px;
          max-width: 190px;
          &:last-child {
            padding-bottom: 50px;
          }
          .option {
            display: flex;
            justify-content: space-between;
            align-items: center;
            .body-type-status {
              margin-left: 10px;
            }
          }
        }
      }
    }
    .photo-frame-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      position: fixed;
      z-index: 1060;
      .photo-frame-dropdown {
        transform: translateY(calc(-50% - 60px));
        position: fixed;
        top: 50%;
        right: -3px;
        color: #FFFFFF;
        display: flex;
        flex-direction: row-reverse;
        align-items: center;
        .photo-image-list-wrapper {
          background: rgba(37, 43, 47, 0.5);
          border: 2px solid #FFFFFF;
          -webkit-backdrop-filter: blur(19px);
          backdrop-filter: blur(19px);
          width: 330px;
          height: 330px;
          border-bottom: none;
          transform: rotate(90deg);
          ul {
            list-style-type: none;
            padding: 0 !important;
            margin: 0 !important;
            height: 100%;
            // width: 100%;
            overflow-y: auto;
            li {
              background: transparent;
              border-bottom: 1px solid #ffffff;
              height: 40px;
              padding: 10px;
              width: 326px !important;
              line-height: 16px;
              .option {
                display: flex;
                justify-content: space-between;
                align-items: center;
                .frame-type, .frame-name {
                  font-weight: 700;
                  font-size: 17px;
                  color: #B8B8B8;
                }
                .frame-type {
                  width: 25%;
                }
                .frame-name {
                  color: #ffffff;
                  width: 55%;
                }
                .frame-status {
                  width: 10%;
                }
                @media screen and (max-width: 667px) {
                  .frame-type, .frame-name {
                    font-size: 15px !important;
                  }
                }
              }
            }
          }
        }
        .photo-frame-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-direction: row;
          background: rgba(37, 43, 47, 0.5);
          border: 2px solid #FFFFFF;
          -webkit-backdrop-filter: blur(19px);
          backdrop-filter: blur(19px);
          border-radius: 22px 0 0 22px;
          height: 330px;
          text-orientation: mixed;
          -ms-writing-mode: tb-rl;
          writing-mode: tb-rl;
          .screenshot-title, .select-angle-btn {
            font-weight: 700;
            text-align: center;
            color: #FFFFFF;
          }
          .screenshot-title {
            height: 100%;
            padding: 10px 0px;
          }
          .select-angle-btn {
            padding: 20px 0px;
            font-size: 14px;
            line-height: 22px;
            border-top: 2px solid #ffffff;
          }
        }
      }
    }
  }
  .confirm-option-wrapper {
    position: fixed;
    left: -15px;
    z-index: 1200;
    top: 50% !important;
    flex-direction: column;
    transform: translateY(calc(-50% - 55px));
    .confirm-option {
      padding-top: 20px !important;
      padding-bottom: 20px !important;
      border-radius: 25px;
      display: flex;
      align-items: center;
      justify-content: center;
      b {
        color: #fff;
        font-size: 17px;
      }
      .save-exit-text {
        padding: 18px 0px 9px 9px;
      }
    }
    .use-this-photo, .take-again {
      background: #252B2F;
      border: 2px solid #FFFFFF;
      border-radius: 20px;
      color: #ffffff;
      text-orientation: mixed;
      writing-mode: vertical-lr;
      height: 170px;
      width: 65px;
      .confirm-text {
        white-space: nowrap;
        margin: 0 0 10px 0;
      }
      .confirm-icon {
        transform: rotate(90deg);
        width: 20px;
        height: 20px;
      }
    }
    .take-again {
      height: 190px;
    }
    .overlay {
      top: -30px;
      .use-this-photo {
        background-color: #05b36d;
      }
    }
  }
}

// .modal-init-upload {
//   .modal-header {
//     padding: 10px !important;
//     border: 0 !important;
//   }
//   .modal-dialog .modal-content {
//     background-color: rgb(55, 179, 114, 0.9) !important;
//     border-radius: 25px !important;
//   }
//   .modal-body {
//     margin: 30px auto;
//     padding: 1.4rem !important;
//     width: 100%;
//     .upload-option-wrapper {
//       display: grid;
//       grid-template-columns: 60% 40%;
//       column-gap: 20px;
//       width: 100%;
//       justify-content: center;
//       .upload-option {
//         padding-top: 1em !important;
//         padding-bottom: 1em !important;
//         background-color: #fff;
//         border-radius: 10px;
//         display: flex;
//         flex-direction: column;
//         justify-content: center;
//         justify-items: center;
//         align-items: center;
//       }
//       .upload-camera {
//         .option-icon {
//           color: #05b36d;
//           font-size: 60px;
//         }
//         b {
//           color: #05b36d;
//         }
//       }
//       .upload-file {
//         .option-icon {
//           color: rgb(112, 112, 112);
//           font-size: 60px;
//         }
//         b {
//           color: rgb(112, 112, 112);
//         }
//       }
//     }
//   }
// }

.bottom-btns {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 30px;
}

.photo-upload-content {
  text-align: center;
  .photo-div {
    position: relative;
    display: flex;
    width: 100%;
    margin-top: 10px;
  }

  .photo-div ol {
    width: 100%;
    position: relative;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  ol,
  ul {
    list-style: none;
  }
  .uploaded-fail {
    cursor: default !important;
  }

  .photo-div ol li {
    width: 100%;
    height: calc(30vw - 0.5rem);
    min-width: 150px;
    overflow: hidden;
    border-radius: 0.5rem;
    img:not(.edit-photo, .upload-fail-icon) {
      height: calc(30vw - 0.5rem);
      object-fit: cover;
      -webkit-user-select: none;
      -webkit-touch-callout: none;
    }
    .img-onUpload {
      opacity: 50%;
    }
  }
  .photo-div ol li:not(:first-child) {
    .upload-fail-text {
      width: 150px;
      justify-content: center;
    }
  }
  .edit-photo {
    z-index: 100;
    position: relative;
    height: 25px;
    width: 25px;
    bottom: 29px;
    cursor: pointer;
  }
  .edit-photo.left {
    float: left;
    left: 5px !important;
  }
  .edit-photo.right {
    float: right;
    right: 5px !important;
  }
  .edit-photo.top {
    bottom: calc(30vw - 0.5rem - 5px);
  }
  .photo-upload {
    background-color: white;

    .upload-section {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: min-content 1fr;
    }
  }
  .photo-upload-header {
    background-color: #e4f6ee;
    text-align: center;
    font-weight: 900;
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
  }
  .upload-btn {
    width: 100%;
    background-color: #e4f6ee !important;
    color: #05b36d !important;
    border-color: #05b36d !important;
  }
  .upload-item {
    background-color: #eee;
    border: 1px solid #05b36d !important;
    cursor: pointer !important;
    overflow: hidden;
    display: inline-block;
  }
  .img-upload-fail {
    background-color: #eee;
  }
  .upload-fail-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    .upload-fail-icon {
      width: 14px !important;
      height: 14px !important;
    }
    p {
      color: #D20319 !important;
      font-size: 11px !important;
      margin-left: 5px;
      margin-bottom: 0;
    }
  }
  .img-upload-progress {
    z-index: 999;
    position: absolute;
    bottom: 10px;
    width: 90%;
    left: 5%;
    height: 2%;
  }

  .alert {
    .alert-container {
      img {
        width: 30px;
      }
    }
  }
  .alert-dismissible {
    font-weight: bold !important;
    font-family: Avenir Next LT W05 Regular !important;
    border-radius: 0.5rem !important;
    .close {
      top: 20% !important;
      transform: translateY(-50%) scale(1.25);
    }
    button {
      font-weight: bold !important;
      font-family: Avenir Next LT W05 Regular !important;
      border-radius: 0.5rem !important;
    }
  }
  .alert-danger {
    color: #FF6868 !important;
    background-color: #FFE4E4 !important;
    border-color: #FF6868 !important;
  }
  .col {
    padding: 0;
  }
  .min-w-222 {
    min-width: 222px;
  }

  .display-when-portrait {
    display: block;
  }
}

.content-title {
  color: #1F8252;
}

@media screen and (orientation: landscape) {
  .display-when-portrait {
    display: none !important;
  }

  .camera-modal {
    .camera-screen {
      #camera-outlay {
        top: 0;
        left: 0;
        height: 100%;
        width: calc(100% - 90px);
        transform: none;
        img {
          width: calc(100vw - 120px);
          height: 90%;
          transform: none;
          padding-right: 60px;
          padding-top: 40px;
        }
      }
      .camera-outlay-buildplate {
        img {
          padding-right: 30px !important;
          padding-left: 35px !important;
          padding-bottom: 25px;
          height: 85% !important;
        }
      }
      .function-section {
        width: 90px;
        height: 100%;
        background: rgba(37, 43, 47, 0.5);
        backdrop-filter: blur(22px);
        right: 0;
        left: unset;
        .shoot-btn {
          bottom: 50%;
          left: unset !important;
          transform: translate(-50px, 50%);
        }
        .button-wrapper {
          flex-direction: column-reverse;
          .under-btn {
            display: flex;
            flex-direction: column;
            .btn:first-child {
              margin: 0px 10px;
            }
            .btn:last-child {
              margin: 10px;
            }
            .body-type-btn, .help-btn {
              transform: none;
            }
          }
          .upper-btn {
              .close-btn {
                transform: none;
              }
          }
        }
      }
      .body-type-dropdown {
        transform: rotate(0deg) translateX(calc(-50% - 43px));
        top: unset;
        left: 50%;
        bottom: -40px;
        ul {
          max-height: 300px;
          overflow-y: auto;
          li {
            height: 40px;
            padding: 10px 20px;
            &:last-child {
              padding-bottom: 50px;
            }
          }
        }
      }
      .photo-frame-wrapper {
        .photo-frame-dropdown {
          top: -3px;
          transform: translateX(calc(-50% - 60px));
          right: unset;
          left: 50%;
          flex-direction: column;
          .photo-image-list-wrapper {
            border-bottom: none;
            border-left: 2px solid #ffffff;
            transform: none;
            ul {
              li {
                .option {
                  .frame-name {
                    margin-left: 10px;
                  }
                }
              }
            }
          }
          @media screen and (max-height: 374px) {
            .photo-image-list-wrapper {
              height: 230px;
            }
          }
          .photo-frame-header {
            height: 50px;
            text-orientation: unset;
            -ms-writing-mode: tb-rl;
            writing-mode: unset;
            width: 330px;
            border-radius: 0 0 22px 22px;
            .screenshot-title {
              width: 100%;
              padding: 0px 10px;
              line-height: 40px;
            }
            .select-angle-btn {
              padding: 0px 20px;
              border-left: 2px solid #ffffff;
              border-top: none;
            }
          }
        }
      }
    }
    .confirm-option-wrapper {
      left: 50%;
      bottom: -20px !important;
      transform: translateX(calc(-50% - 40px));
      top: unset !important;
      flex-direction: row;
      height: 65px;
      width: 400px;
      .confirm-option {
        padding-top: 15px !important;
        padding-bottom: 25px !important;
        b {
          color: #fff;
          font-size: 17px;
        }
        .save-exit-text {
          padding: 18px 0px 167px 9px !important;
        }
      }
      .use-this-photo, .take-again {
        text-orientation: unset;
        writing-mode: unset;
        width: 170px;
        height: 65px;
        .confirm-text {
          margin: 0 10px 0 0;
        }
        .confirm-icon {
          transform: none;
        }
      }
      .take-again {
        padding-right: 10px;
        width: 190px;
      }
      .overlay {
        top: unset;
        left: -30px;
      }
    }
  }
}

.nudge {
  .heading{
    margin-bottom: 0;
    font-weight: bold !important;
  }
  .alert-heading {
    font-weight: normal !important;
    color: #463209;
    background-color: #fff6ed;
    border: 1px solid #F5CE84;
    box-sizing: border-box;
    border-radius: 10px;
    width: 98%;
    margin: 10px auto;
    padding: 10px 0;
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;

    p {
      margin-bottom: 0 !important;
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        margin-right: 5px;
      }
    }
  }
}

@media screen and(min-width: 900px) {
  .photo-upload {
    .photo-upload-wrapper {
      padding-left: 40px !important;
      padding-right: 40px !important;

      .upload-section {
        .upload-box-container {
          padding-left: 30px;
          padding-right: 30px;
        }
        .upload-tips {
          margin-left: 30px;
          margin-right: 30px;
        }
      }
    }
    .btn-group-wrapper {
      margin-top: 0.5rem !important;
    }
    .forward-btn {
      width: auto;
      position: absolute;
      right: 190px;
      bottom: 55px;
      background-color: #ffffff !important;
    }
    .alert-dismissible {
      .close {
        top: 5% !important;
        transform: scale(1.5) !important;
        z-index: 100000;
      }
    }
    .alert-danger {
      margin: 0;
    }
    .photo-div ol li {
      width: 170px;
      height: 110px;
      min-width: unset;
      z-index: 100000;
      position: relative;

      img:not(.edit-photo, .upload-fail-icon) {
        height: 92px !important;
      }

      .edit-photo {
        width: 18px;
        height: 18px;
        bottom: 23px;
      }

      .edit-photo.top {
        bottom: calc(92px - 5px);
      }
    }

    .photo-div .photo-list {
      grid-template-columns: repeat(3, 170px);
      gap: 15px;
      column-gap: 24px;
      .item {
        height: 109px !important;
        min-width: unset;
        z-index: 100000;
        position: relative;
  
        img:not(.edit-photo, .upload-fail-icon) {
          height: 109px !important;
        }
  
        .edit-photo {
          width: 18px;
          height: 18px;
          bottom: 23px;
        }
  
        .edit-photo.top {
          bottom: calc(109px - 5px) !important;
        }
      }
    }

    .photo-div {
      margin-top: 20px;
      .wide-list {
        width: auto !important;
        padding: 0 1rem !important;
        margin-left: 0.8rem;
      }
    }
  }

  #drag-files,
  .upload-item input[type="file"] {
    z-index: 99999;
  }

  .nudge {
    .alert-dismissible {
      .close {
        top: 20% !important;
      }
    }
    .heading {
      font-size: 20px;
    }
    .alert-heading {
      font-weight: bold !important;
      font-size: 16px;
    }
  }
}

@media screen and(max-width: 386px) {
  .photo-description {
    padding: 10px !important;
  }
}

@media screen and(min-width: 768px) {
  .photo-upload-content {
    .btn-group-wrapper.custom-padding-1 {
      // 100vh - header - stepper - content - button group height
      padding-bottom: max(calc(100vh - 75px - 102px - 520px - 64px), 40px);
    }

    .btn-group-wrapper.custom-padding-2 {
      padding-bottom: max(calc(100vh - 75px - 102px - 590px - 64px), 40px);
    }

    .btn-group-wrapper.custom-padding-3 {
      position: sticky !important;
      padding-bottom: 0 !important;
      padding-left: 30px !important;
      padding-right: 30px !important;
    }
  }
}

@media screen and(min-width: 991px) {
  .photo-upload-content .photo-upload {
    .upload-section {
      grid-template-columns: auto 50%;
  
      .content-title, .upload-tips {
        grid-column-start: 1;
        grid-column-end: 2;
      }
  
      .upload-box-container {
        grid-column-start: 2;
        grid-row-start: 1;
        grid-row-end: 3;
      }
  
      .upload-tips {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        margin-top: 10px;
      }
    }
    .photo-div .photo-list {
      grid-template-columns: repeat(4, 170px);
      column-gap: 39px;
      row-gap: 20px;
    }
  }
}

@media screen and(min-width: 1200px) {
  .photo-upload-content .photo-upload .photo-div .photo-list {
    grid-template-columns: repeat(5, 170px);
    column-gap: 32px;
  }
}
</style>
